import { log } from '../logs'
import { WATERMARK_DRAWN_KEY, FAIL_ALERTED_KEY, OMNI_PREVIEW_TOKEN_KEY } from '../constants/preview'

/**
 * 格式化过期时间为可读字符串
 * @param expiresAt - 过期时间戳（毫秒）或 ISO 8601 格式字符串
 * @returns 格式化后的时间字符串，格式为 "YYYY-MM-DD HH:mm:ss"
 * @example
 * formatExpireTime(1705276800000) // "2024-01-15 00:00:00"
 * formatExpireTime("2024-01-15T00:00:00Z") // "2024-01-15 08:00:00"
 */
export function formatExpireTime(expiresAt: number | string): string {
  try {
    let date: Date

    if (typeof expiresAt === 'number') {
      // 处理时间戳（毫秒）
      date = new Date(expiresAt * 1000)
    } else if (typeof expiresAt === 'string') {
      // 处理 ISO 8601 格式字符串
      date = new Date(expiresAt)
    } else {
      log.error('formatExpireTime: 无效的 expiresAt 参数类型', expiresAt)
      return '无效时间'
    }

    // 验证日期是否有效
    if (isNaN(date.getTime())) {
      log.error('formatExpireTime: 无效的日期值', expiresAt)
      return '无效时间'
    }

    // 格式化为本地时间字符串
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')

    const formattedTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    log.debug('formatExpireTime: 时间格式化成功', { expiresAt, formattedTime })

    return formattedTime
  } catch (error) {
    log.error('formatExpireTime: 格式化时间时发生错误', error)
    return '无效时间'
  }
}

/**
 * 检查水印是否已绘制
 * @returns 如果水印已绘制返回 true，否则返回 false
 * @example
 * isWatermarkDrawn() // false
 * setWatermarkDrawn()
 * isWatermarkDrawn() // true
 */
export function isWatermarkDrawn(): boolean {
  try {
    const isDrawn = window[WATERMARK_DRAWN_KEY] === true
    log.debug('isWatermarkDrawn: 检查水印绘制状态', { isDrawn })
    return isDrawn
  } catch (error) {
    log.error('isWatermarkDrawn: 检查水印状态时发生错误', error)
    return false
  }
}

/**
 * 检查过期提醒是否已弹出
 * @returns 如果过期提醒已弹出返回 true，否则返回 false
 * @example
 * isExpireAlerted() // false
 * setExpireAlerted()
 * isExpireAlerted() // true
 */
export function isExpireAlerted(): boolean {
  try {
    const isAlerted = window[FAIL_ALERTED_KEY] === true
    log.debug('isExpireAlerted: 检查过期提醒状态', { isAlerted })
    return isAlerted
  } catch (error) {
    log.error('isExpireAlerted: 检查过期提醒状态时发生错误', error)
    return false
  }
}

/**
 * 设置水印已绘制状态
 * @param drawn - 是否已绘制，默认为 true
 * @example
 * setWatermarkDrawn() // 设置为 true
 * setWatermarkDrawn(false) // 设置为 false
 */
export function setWatermarkDrawn(drawn: boolean = true): void {
  try {
    window[WATERMARK_DRAWN_KEY] = drawn
    log.debug('setWatermarkDrawn: 设置水印绘制状态', { drawn })
  } catch (error) {
    log.error('setWatermarkDrawn: 设置水印状态时发生错误', error)
  }
}

/**
 * 设置过期提醒已弹出状态
 * @param alerted - 是否已提醒，默认为 true
 * @example
 * setExpireAlerted() // 设置为 true
 * setExpireAlerted(false) // 设置为 false
 */
export function setExpireAlerted(alerted: boolean = true): void {
  try {
    window[FAIL_ALERTED_KEY] = alerted
    log.debug('setExpireAlerted: 设置过期提醒状态', { alerted })
  } catch (error) {
    log.error('setExpireAlerted: 设置过期提醒状态时发生错误', error)
  }
}

/**
 * 从 localStorage 中移除 omni_preview_token
 * @example
 * removeOmniPreviewTokenFromLocalStorage()
 */
export function removeOmniPreviewTokenFromLocalStorage(): void {
  try {
    localStorage.removeItem(OMNI_PREVIEW_TOKEN_KEY)
    log.debug('removeOmniPreviewTokenFromLocalStorage: 已移除 localStorage 中的 omni_preview_token')
  } catch (error) {
    log.error('removeOmniPreviewTokenFromLocalStorage: 移除 token 时发生错误', error)
  }
}

/**
 * 从 URL 中移除 omni_preview_token 参数（包括 search 和 hash）
 * @param url - 可选的 URL 字符串，如果不传则使用当前页面 URL
 * @returns 清理后的 URL 字符串
 * @example
 * removeOmniPreviewTokenFromURL() // 使用当前页面 URL
 * removeOmniPreviewTokenFromURL('https://example.com/?omni_preview_token=xxx') // 使用指定 URL
 */
export function removeOmniPreviewTokenFromURL(url?: string): string {
  try {
    const targetURL = url || window.location.href
    const urlObj = new URL(targetURL)

    // 移除 search 参数中的 omni_preview_token
    urlObj.searchParams.delete(OMNI_PREVIEW_TOKEN_KEY)

    // 处理 hash 中的参数
    if (urlObj.hash) {
      const hashWithoutPrefix = urlObj.hash.startsWith('#') ? urlObj.hash.slice(1) : urlObj.hash
      const queryStart = hashWithoutPrefix.indexOf('?')

      if (queryStart !== -1) {
        // hash 中包含查询参数
        const hashPath = hashWithoutPrefix.slice(0, queryStart)
        const queryString = hashWithoutPrefix.slice(queryStart + 1)
        const params = new URLSearchParams(queryString)
        params.delete(OMNI_PREVIEW_TOKEN_KEY)

        // 重建 hash
        const newQueryString = params.toString()
        urlObj.hash = newQueryString ? `${hashPath}?${newQueryString}` : hashPath
      }
    }

    const cleanedURL = urlObj.toString()
    log.debug('removeOmniPreviewTokenFromURL: 已清理 URL 中的 omni_preview_token', { cleanedURL })
    return cleanedURL
  } catch (error) {
    log.error('removeOmniPreviewTokenFromURL: 清理 URL 时发生错误', error)
    return url || window.location.href
  }
}
